import { useState, useCallback, useEffect, useMemo, useContext } from 'react';
import { Modal, IndexTable, Text, Box, Checkbox, TextField, InlineStack, Spinner, RadioButton, Badge } from '@shopify/polaris';
import { useToast } from '#client/components/common/Toast/ToastContext';
import { zebraPrintService, ZebraPrintError } from '#client/services/zebraPrintService';
import { SessionContext } from '#client/contexts/SessionContext.jsx';
import { LabelPrintMethodSelect } from '#client/components/features/LabelPrinting/components/LabelPrintMethodSelect';
import { ZebraPrinterPicker } from '#client/components/features/LabelPrinting/components/ZebraPrinterPicker';
import { LabelCatalogSelect } from '#client/components/features/LabelPrinting/components/LabelCatalogSelect';
import { useLabelPrintersQuery } from '#client/components/features/LabelPrinting/hooks/useLabelPrintersQuery';
import { useLabelPricesQuery } from '#client/components/features/LabelPrinting/hooks/useLabelPricesQuery';
import { useZebraPrinterSelection } from '#client/components/features/LabelPrinting/hooks/useZebraPrinterSelection';
import {
    buildProductLabelsPdfUrl,
    catalogsForLocation,
    countTotalLabels,
    defaultPriceSelectionFor,
    formatLabelPrice,
    hasCatalogPriceOverride,
    priceForSelection,
    priceListIdFor,
} from '#client/components/features/LabelPrinting/utils/productLabels';
import styles from './PrintLabelsModal.module.css';

export default function PrintLabelsModal({ open, onClose, variants }) {
    const { addToast } = useToast();
    const { currentUser } = useContext(SessionContext);
    const [selectedSkus, setSelectedSkus] = useState([]);
    const [searchValue, setSearchValue] = useState('');
    const [printMode, setPrintMode] = useState('pdf'); // 'pdf' | 'zebra'
    const [isLoading, setIsLoading] = useState(false);
    const [quantities, setQuantities] = useState({}); // sku -> quantity map
    const [skuType, setSkuType] = useState('sku');
    const [priceSelection, setPriceSelection] = useState('');

    const showPrinterForm = printMode === 'zebra';

    // For future: enable multiple print methods
    const enableZebraCloudPrinting = true;

    const labelPrintersQuery = useLabelPrintersQuery({ enabled: enableZebraCloudPrinting && open });

    const zebraSelection = useZebraPrinterSelection({
        enabled: enableZebraCloudPrinting && open,
        locations: labelPrintersQuery.locations,
        defaultLocationGid: currentUser?.activeStore,
    });

    const resetZebraSelection = zebraSelection.resetSelection;
    const zebraSelectedPrinterDeviceId = zebraSelection.selectedPrinterDeviceId;
    const getZebraSelectionError = zebraSelection.getSelectionError;

    // Picks the Zebra printer's store, and which catalogs may price the labels.
    const selectedLocationGid = zebraSelection.selectedLocationGid;

    const priceListId = priceListIdFor(priceSelection);

    // Rows here come from the purchase order, not product search, so the prices that will
    // print have to be looked up separately.
    const variantSkus = useMemo(() => variants.map((v) => v.sku), [variants]);

    const labelPricesQuery = useLabelPricesQuery({ enabled: open, skus: variantSkus });

    const labelPricesBySku = labelPricesQuery.pricesBySku;
    const catalogs = labelPricesQuery.catalogs;

    // Reset the catalog choice whenever the location changes: a catalog that no longer
    // covers the location would otherwise keep pricing labels the register never rings.
    // A location with one applicable catalog gets it outright; several leaves it unset so
    // the operator has to choose.
    useEffect(() => {
        if (!open) return;

        setPriceSelection((current) => {
            const stillApplies = current && catalogsForLocation(catalogs, selectedLocationGid).some((catalog) => catalog.priceListId === current);
            return stillApplies ? current : defaultPriceSelectionFor(catalogs, selectedLocationGid);
        });
    }, [catalogs, open, selectedLocationGid]);

    // Load saved printers when modal opens
    useEffect(() => {
        if (open) {
            // Initialize quantities from variants
            const initialQuantities = {};
            variants.forEach((v) => {
                initialQuantities[v.sku] = v.quantity || 0;
            });
            setQuantities(initialQuantities);
        } else {
            // Reset state when modal closes
            setSelectedSkus([]);
            setSearchValue('');
            setPrintMode('pdf');
            setPriceSelection('');
            resetZebraSelection();
            setQuantities({});
        }
    }, [open, resetZebraSelection, variants]);

    // Filter variants by search value
    const filteredVariants = useMemo(() => {
        if (!searchValue) return variants;
        const lower = searchValue.toLowerCase();
        return variants.filter(
            (v) => (v.productName && v.productName.toLowerCase().includes(lower)) || (v.sku && v.sku.toLowerCase().includes(lower)) || (v.amazonSku && v.amazonSku.toLowerCase().includes(lower))
        );
    }, [variants, searchValue]);

    // Handle individual checkbox change
    const handleCheckboxChange = useCallback((sku, checked) => {
        setSelectedSkus((prev) => {
            if (checked) {
                return [...prev, sku];
            } else {
                return prev.filter((s) => s !== sku);
            }
        });
    }, []);

    // Handle select all toggle
    const handleSelectAll = useCallback(
        (checked) => {
            if (checked) {
                // Only select variants with quantity > 0 and amazonSku if FNSKU mode
                const selectableSkus = filteredVariants
                    .filter((v) => {
                        const hasQuantity = (quantities[v.sku] || 0) > 0;
                        const hasAmazonSku = !!v.amazonSku;
                        const hasBarcode = skuType === 'sku' ? v.barcode : hasAmazonSku;

                        return hasQuantity && hasBarcode;
                    })
                    .map((v) => v.sku);
                setSelectedSkus(selectableSkus);
            } else {
                setSelectedSkus([]);
            }
        },
        [filteredVariants, quantities, skuType]
    );

    // Check if all items are selected (excluding items with 0 quantity or missing amazonSku in FNSKU mode)
    const allSelected = useMemo(() => {
        const selectableVariants = filteredVariants.filter((v) => {
            const hasQuantity = (quantities[v.sku] || 0) > 0;
            const hasAmazonSku = !!v.amazonSku;
            const hasBarcode = skuType === 'sku' ? v.barcode : hasAmazonSku;
            return hasQuantity && hasBarcode;
        });
        return selectableVariants.length > 0 && selectableVariants.every((v) => selectedSkus.includes(v.sku));
    }, [filteredVariants, selectedSkus, quantities, skuType]);

    // Handle search change
    const handleSearchChange = useCallback((value) => {
        setSearchValue(value);
    }, []);

    // Handle quantity change
    const handleQuantityChange = useCallback((sku, value) => {
        // Only allow positive integers
        if (value === '' || /^[1-9][0-9]*$/.test(value)) {
            setQuantities((prev) => ({
                ...prev,
                [sku]: value === '' ? '' : parseInt(value, 10),
            }));
        }
    }, []);

    // Handle print labels - supports both PDF and Zebra printing
    const handlePrintLabels = useCallback(async () => {
        if (selectedSkus.length === 0) {
            addToast({
                type: 'error',
                message: 'Please select at least one item to print labels',
            });
            return;
        }

        // A location in several catalogs leaves the choice unset, so nothing prints until
        // the operator picks one.
        if (!priceSelection) {
            addToast({
                type: 'error',
                message: 'Please choose a price catalog',
            });
            return;
        }

        // PDF printing (default)
        if (printMode === 'pdf') {
            try {
                const pdfUrl = buildProductLabelsPdfUrl({ skus: selectedSkus, quantitiesBySku: quantities, skuType, priceListId });
                window.open(pdfUrl, '_blank');

                const totalLabels = countTotalLabels({ skus: selectedSkus, quantitiesBySku: quantities });

                addToast({
                    type: 'success',
                    message: `Successfully opened ${totalLabels} label${totalLabels > 1 ? 's' : ''}`,
                });

                setSelectedSkus([]);
                setSearchValue('');
                onClose();
            } catch (error) {
                console.error('Error opening product labels:', error);
                addToast({
                    type: 'error',
                    message: 'Failed to open product labels',
                });
            }
            return;
        }

        // Zebra SendFileToPrinter printing
        const zebraSelectionError = getZebraSelectionError();
        if (zebraSelectionError) {
            addToast({
                type: 'error',
                message: zebraSelectionError,
            });
            return;
        }

        try {
            setIsLoading(true);

            await zebraPrintService.printLabels(
                selectedSkus.map((sku) => ({ sku, quantity: quantities[sku] || 0 })),
                zebraSelectedPrinterDeviceId,
                { skuType, priceListId }
            );

            const totalLabels = countTotalLabels({ skus: selectedSkus, quantitiesBySku: quantities });

            addToast({
                type: 'success',
                message: `Successfully sent ${totalLabels} label${totalLabels > 1 ? 's' : ''} to printer`,
            });

            setSelectedSkus([]);
            setSearchValue('');
            onClose();
        } catch (error) {
            if (error instanceof ZebraPrintError) {
                addToast({
                    type: 'error',
                    message: error.getUserFriendlyMessage(),
                });
                console.error('[PrintLabelsModal] Zebra error:', error.code, error.details);
            } else {
                addToast({
                    type: 'error',
                    message: 'Failed to print labels: ' + (error.message || 'Unknown error'),
                });
                console.error('[PrintLabelsModal] Print error:', error);
            }
        } finally {
            setIsLoading(false);
        }
    }, [addToast, enableZebraCloudPrinting, getZebraSelectionError, onClose, priceListId, priceSelection, printMode, quantities, selectedSkus, skuType, zebraSelectedPrinterDeviceId]);

    // Handle modal close
    const handleClose = useCallback(() => {
        onClose();
    }, [onClose]);

    // Table headings
    const headings = [{ title: 'Select' }, { title: 'Product Name' }, { title: skuType === 'fnsku' ? 'Amazon FNSKU' : 'SKU' }, { title: 'Price' }, { title: 'Quantity' }];

    // Table rows
    const rowMarkup = filteredVariants.map((variant, index) => {
        const isChecked = selectedSkus.includes(variant.sku);
        const quantity = quantities[variant.sku] || 0;
        const hasAmazonSku = !!variant.amazonSku;
        const hasBarcode = skuType === 'sku' ? variant.barcode : hasAmazonSku;
        const isSelectable = quantity > 0 && hasBarcode;
        const labelPrice = labelPricesBySku.get(variant.sku);
        const printedPrice = labelPrice ? priceForSelection(labelPrice, priceSelection).amount : null;
        const isCatalogPrice = hasCatalogPriceOverride(printedPrice, labelPrice?.defaultPrice);
        return (
            <IndexTable.Row id={variant.sku} key={variant.sku} position={index}>
                <IndexTable.Cell>
                    <Checkbox label="Select item" labelHidden checked={isChecked} onChange={(checked) => handleCheckboxChange(variant.sku, checked)} disabled={!isSelectable} />
                </IndexTable.Cell>
                <IndexTable.Cell>
                    <span className={styles.productName}>{variant.productName || 'Unknown Product'}</span>
                </IndexTable.Cell>
                <IndexTable.Cell>
                    <Text as="span" tone={skuType === 'fnsku' && !variant.amazonSku ? 'critical' : undefined}>
                        {skuType === 'fnsku' ? variant.amazonSku || 'No FNSKU - cannot print' : variant.sku || 'N/A'}
                    </Text>
                </IndexTable.Cell>
                <IndexTable.Cell>
                    {labelPrice ? (
                        <InlineStack gap="150" blockAlign="center" wrap={false}>
                            <Text as="span" fontWeight={isCatalogPrice ? 'semibold' : undefined}>
                                {formatLabelPrice(printedPrice)}
                            </Text>
                            {isCatalogPrice && (
                                <>
                                    <Badge tone="info">Catalog</Badge>
                                    <Text as="span" variant="bodySm" tone="subdued">
                                        was {formatLabelPrice(labelPrice.defaultPrice)}
                                    </Text>
                                </>
                            )}
                        </InlineStack>
                    ) : (
                        <Text as="span" tone="subdued">
                            {labelPricesQuery.isPending ? '—' : 'Not found'}
                        </Text>
                    )}
                </IndexTable.Cell>
                <IndexTable.Cell>
                    <span className={styles.quantityInput}>
                        <TextField label="Quantity" labelHidden type="number" value={String(quantity)} onChange={(value) => handleQuantityChange(variant.sku, value)} min="0" autoComplete="off" />
                    </span>
                </IndexTable.Cell>
            </IndexTable.Row>
        );
    });

    return (
        <Modal
            open={open}
            onClose={handleClose}
            title="Print Product Labels"
            primaryAction={{
                content: 'Print Labels',
                onAction: handlePrintLabels,
                disabled: selectedSkus.length === 0,
            }}
            secondaryActions={[
                {
                    content: 'Cancel',
                    onAction: handleClose,
                },
            ]}
            large
        >
            <Modal.Section>
                {enableZebraCloudPrinting && (
                    <Box paddingBlockEnd="400">
                        <LabelPrintMethodSelect value={printMode} onChange={setPrintMode} enableZebraOption={enableZebraCloudPrinting} />
                    </Box>
                )}

                {/* Shown for both print methods: the location decides which Market catalog
                    price prints on the label. */}
                <Box paddingBlockEnd="400">
                    <ZebraPrinterPicker
                        isLoading={labelPrintersQuery.isLoading}
                        locationOptions={zebraSelection.locationOptions}
                        printerOptions={zebraSelection.printerOptions}
                        selectedLocationGid={zebraSelection.selectedLocationGid}
                        onLocationChange={zebraSelection.setSelectedLocationGid}
                        selectedPrinterDeviceId={zebraSelection.selectedPrinterDeviceId}
                        onPrinterChange={zebraSelection.setSelectedPrinterDeviceId}
                        printerDisabled={!zebraSelection.selectedLocationGid || zebraSelection.availablePrinters.length === 0}
                        printerHelpText={zebraSelection.printerHelpText}
                        showPrinter={showPrinterForm}
                        locationHelpText="Sets the Zebra printer, and which price catalogs apply."
                    />
                </Box>

                <Box paddingBlockEnd="400">
                    <LabelCatalogSelect catalogs={catalogs} locationGid={selectedLocationGid} value={priceSelection} onChange={setPriceSelection} />
                </Box>

                <Box paddingBlockEnd="400">
                    <InlineStack align="space-between" blockAlign="center">
                        <Checkbox label={allSelected ? 'Deselect All' : 'Select All'} checked={allSelected} onChange={handleSelectAll} />
                        <Text as="span" variant="bodySm" tone="subdued">
                            {selectedSkus.length} of {variants.length} selected
                        </Text>
                    </InlineStack>
                </Box>

                <Box paddingBlockEnd="400">
                    <InlineStack gap="200">
                        <RadioButton label="Work World SKU" checked={skuType === 'sku'} id="sku-modal" name="skuType" onChange={() => setSkuType('sku')} />
                        <RadioButton label="Amazon FNSKU" checked={skuType === 'fnsku'} id="fnsku-modal" name="skuType" onChange={() => setSkuType('fnsku')} />
                    </InlineStack>
                </Box>

                <Box paddingBlockEnd="400">
                    <TextField label="Search items" value={searchValue} onChange={handleSearchChange} placeholder="Search by product name or SKU" autoComplete="off" labelHidden />
                </Box>

                {isLoading && (
                    <Box paddingBlockEnd="400">
                        <InlineStack align="center">
                            <Spinner accessibilityLabel="Sending print job" size="small" />
                            <Text as="span" variant="bodySm">
                                Sending to printer...
                            </Text>
                        </InlineStack>
                    </Box>
                )}

                <IndexTable
                    resourceName={{
                        singular: 'item',
                        plural: 'items',
                    }}
                    itemCount={filteredVariants.length}
                    headings={headings}
                    selectable={false}
                >
                    {rowMarkup}
                </IndexTable>

                {filteredVariants.length === 0 && (
                    <Box paddingBlock="400">
                        <Text as="p" alignment="center" tone="subdued">
                            No items found matching your search
                        </Text>
                    </Box>
                )}
            </Modal.Section>
        </Modal>
    );
}
